'use client';

import type { DatePickerProps as ReactDatePickerProps } from "react-datepicker"
import dynamic from "next/dynamic";
import { useState, type ComponentType } from "react";
import AppIcon from "@/app/components/atomic/AppIcon";
import { formatDateInput } from "@/app/utils/helpers";

// Register the French locale client-side (lazy-loaded) so labels aren't English.
const DatePicker = dynamic<ReactDatePickerProps>(
    async () => {
        // Reuse react-datepicker's own date-fns rather than a duplicate top-level dep.
        const [reactDatePicker, { fr }] = await Promise.all([
            import("react-datepicker"),
            import("date-fns/locale/fr"),
        ])
        reactDatePicker.registerLocale("fr", fr)
        return reactDatePicker.default as unknown as ComponentType<ReactDatePickerProps>
    },
    {
        loading: () => <div className="min-h-12 w-full" aria-hidden />,
    }
)

interface DatePickerProps {
    value: string;
    /** Id posé sur l'input, pour l'associer à un <label htmlFor>. */
    id?: string;
    minDate?: string;
    maxDate?: string;
    onChange: any;
    className?: string;
    wrapperClassName?: string;
    displayIcon?: boolean;
    useIso?: boolean;
    disabled?: boolean;
    filterDate?: ReactDatePickerProps['filterDate'];
    /** Error state for the field */
    error?: { message?: string };
    /** Placeholder text for the input mask */
    placeholder?: string;
    /** Use the loan-comparator design */
    variant?: 'default' | 'loan' | 'v2' | 'health' | 'travel';
    /** Open the calendar popup automatically on mount */
    autoOpen?: boolean;
    /** Date-of-birth mode: stepped year → month → day cascade. */
    birthdate?: boolean;
    /** Beige/neutral theme on a non-birthdate picker, for coherence with the date-of-birth pickers. */
    beige?: boolean;
    /** Renders the calendar in a body-level portal: required inside a scrollable modal, which would clip it. */
    portalId?: string;
    /** Replaces the text input with a custom trigger, e.g. a button showing the date in long form. */
    customInput?: ReactDatePickerProps['customInput'];
}

type DobView = 'year' | 'month' | 'day';

export default function AppDatePicker({
    value,
    id,
    minDate,
    maxDate,
    onChange,
    displayIcon = false,
    useIso = false,
    filterDate = () => true,
    className = '',
    wrapperClassName = '',
    disabled = false,
    error,
    placeholder = '__/__/____',
    variant = 'default',
    autoOpen = false,
    birthdate = false,
    beige = false,
    portalId,
    customInput,
}: DatePickerProps) {
    const isLoan = variant === 'loan';
    const isV2 = variant === 'v2';
    const isHealth = variant === 'health';
    const isTravel = variant === 'travel';
    const useBeigeTheme = birthdate || beige;

    // Cascade step shown, plus the draft positioning the month/day grids.
    const [dobView, setDobView] = useState<DobView>('year');
    const [dobDraft, setDobDraft] = useState<Date | null>(null);

    const handleChange = (date: Date | null) => {
        // react-datepicker passes null when the field is cleared (or the typed
        // value is incomplete/invalid) — clear the value instead of crashing.
        if (!date) {
            onChange('');
            return;
        }
        if (useIso) {
            const formattedDate = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
            onChange(formattedDate);
        } else {
            onChange(date.toLocaleDateString('fr-FR'));
        }
    }

    // Auto-insert the JJ/MM/AAAA slashes while typing; react-datepicker reads target.value after this runs.
    const handleChangeRaw = (event?: React.SyntheticEvent) => {
        const target = event?.target
        // onChangeRaw also fires on calendar clicks, where the target is a day cell.
        if (!(target instanceof HTMLInputElement)) return
        const raw = target.value
        // Reorder a pasted ISO date so it isn't mangled into JJ/MM/AAAA digits as-is.
        const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/)
        const masked = formatDateInput(iso ? `${iso[3]}${iso[2]}${iso[1]}` : raw)
        if (masked !== raw) target.value = masked
    }

    const parseDate = (dateString: string) => {
        if (!dateString) return new Date();
        if (typeof dateString === 'object') return dateString;

        try {
            if (useIso) {
                if (dateString.includes('-')) {
                    const [year, month, day] = dateString.split('-');
                    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
                }
                return new Date(dateString);
            }

            const [day, month, year] = dateString.split('/');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day), 12, 0, 0);
        } catch (e) {
            console.error("Date parsing error:", e);
            return new Date();
        }
    }

    let minDateObj = minDate ? parseDate(minDate) : undefined;
    let maxDateObj = maxDate ? parseDate(maxDate) : undefined;

    // Where the year grid opens when empty: current value, else ~30 years ago.
    const birthdateDefaultOpen = (() => {
        if (value) return parseDate(value);
        const d = new Date();
        d.setFullYear(d.getFullYear() - 30);
        d.setHours(0, 0, 0, 0);
        // Clamp the opening year into the allowed range (e.g. a child's 0–26 window).
        if (maxDateObj && d > maxDateObj) return maxDateObj;
        if (minDateObj && d < minDateObj) return minDateObj;
        return d;
    })();
    const birthdateOpenTo = birthdate ? (dobDraft ?? birthdateDefaultOpen) : undefined;

    const resetDobCascade = () => {
        // Reopen on the day step when a date is set; full cascade only when empty.
        setDobView(value ? 'day' : 'year');
        setDobDraft(value ? parseDate(value) : null);
    };

    // Advance on calendar click only — onSelect skips typing, so manual entry still commits via onChange.
    const handleDobSelect = (date: Date | null) => {
        if (!date) return;
        setDobDraft(date);
        setDobView((v) => (v === 'year' ? 'month' : 'day'));
    };

    const isDateBlocked = (date: any) => {
        return !filterDate(date)
    }

    const borderClass = error
        ? 'border-invalidRed'
        : isTravel
        ? 'border-rm-gray-light hover:border-rm-blue focus-within:border-rm-blue'
        : isV2
        ? 'border-rm-gray-light hover:border-rm-gray focus-within:border-rm-gray'
        : 'border-blue-300 hover:border-blue-500 focus-within:border-blue-500';

    const wrapperHeightClass = isHealth || isV2 ? 'min-h-12' : isTravel ? 'h-[52px]' : 'h-10';
    const wrapperRadiusClass = isTravel ? 'rounded-xl' : 'rounded-rm-10';
    const innerInputClass = isLoan
        ? 'text-blue-700 font-semibold text-[14px]'
        : isHealth
        ? 'text-blue-900 text-[14px] py-3'
        : isTravel
        ? 'text-[15px] text-pale px-4'
        : isV2
        ? 'text-pale'
        : '';

    return (
        <div>
            <div
                className={`flex items-center border ${borderClass} ${wrapperRadiusClass} overflow-hidden w-full ${wrapperHeightClass} bg-white transition-all duration-300 ${wrapperClassName}`}
            >
                <DatePicker
                    id={id}
                    selected={value ? parseDate(value) : undefined}
                    onChange={handleChange}
                    onChangeRaw={handleChangeRaw}
                    onSelect={birthdate ? handleDobSelect : undefined}
                    minDate={minDateObj}
                    maxDate={maxDateObj}
                    dateFormat="dd/MM/yyyy"
                    dateFormatCalendar="LLLL yyyy"
                    filterDate={filterDate}
                    locale="fr"
                    placeholderText={placeholder}
                    className={`flex w-full p-2 focus:outline-none ${innerInputClass} ${className}`}
                    calendarClassName={`rounded-lg border ${isV2 || isTravel ? 'border-rm-gray-light' : 'border-blue-300'}${useBeigeTheme ? ' rm-datepicker-beige' : ''}${birthdate ? ' dob-cascade' : ''}`}
                    dayClassName={(date) => {
                        return isDateBlocked(date) ? (isV2 || isTravel ? 'bg-rm-gray-light opacity-50' : 'bg-blue-200 opacity-50') : ''
                    }}
                    wrapperClassName="w-full"
                    portalId={portalId}
                    customInput={customInput}
                    disabled={disabled}
                    strictParsing={false}
                    // Birthdate: stepped cascade; other pickers keep the day grid + dropdowns.
                    showYearPicker={birthdate && dobView === 'year'}
                    showMonthYearPicker={birthdate && dobView === 'month'}
                    showMonthDropdown={!birthdate}
                    showYearDropdown={!birthdate}
                    dropdownMode="select"
                    shouldCloseOnSelect={!birthdate || dobView === 'day'}
                    openToDate={birthdateOpenTo}
                    onCalendarOpen={birthdate ? resetDobCascade : undefined}
                    onCalendarClose={birthdate ? resetDobCascade : undefined}
                    // Month/day steps: header month/year jump back a step; year step keeps the built-in header.
                    renderCustomHeader={
                        birthdate && dobView !== 'year'
                            ? ({ monthDate }: { monthDate: Date }) => (
                                <div className="dob-cascade-header">
                                    {dobView === 'day' && (
                                        <button
                                            type="button"
                                            className="dob-cascade-step"
                                            onClick={() => setDobView('month')}
                                        >
                                            {monthDate.toLocaleDateString('fr-FR', { month: 'long' })}
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className="dob-cascade-step"
                                        onClick={() => setDobView('year')}
                                    >
                                        {monthDate.getFullYear()}
                                    </button>
                                </div>
                            )
                            : undefined
                    }
                    autoFocus={autoOpen}
                />

                {displayIcon && isTravel && (
                    <div className="flex h-full w-12 shrink-0 items-center justify-center bg-neutral">
                        <i className="far fa-calendar text-[17px] text-[#4a5565]" aria-hidden />
                    </div>
                )}
                {(displayIcon || isHealth) && !isTravel && (
                    <div className={`flex items-center justify-center ${isHealth || isV2 ? 'min-h-12 px-3' : 'h-10 px-2'} text-[14px] font-semibold border-l ${isV2 ? 'bg-neutral border-rm-gray-light' : 'bg-blue-100 border-blue-300'}`}>
                        <AppIcon
                            size="sm"
                            type="regular"
                            iconClassName={`fa-calendar ${isV2 ? 'text-pale' : isHealth ? 'text-blue-600' : 'text-blue-700'}`}
                        />
                    </div>
                )}
            </div>
            {error?.message && (
                <p className="mt-1 text-[12px] text-red-600">{error.message}</p>
            )}
        </div>
    )
}
